// src/pages/accounts/OpeningBalances.jsx
// D-296 (09 Oct 2026) — Opening balances when a company starts on Zeno.
//   1. Vendor / subcontractor: what we owed them (or paid in advance) on the
//      go-live date → shows in Payables as the oldest bill / an advance.
//   2. Cash / Bank / UPI / Cheque: money in hand on the go-live date → the
//      Cash / Bank Book cards start from it.
// Not income, not expense — Accounts totals, Home and Reports do not change.
// Writes only through src/services/openingBalanceService.js. A wrong entry is
// reversed (never edited) and entered again.

import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { canDoAccounts } from "../../services/purchaseBillService";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  newOpeningId, recordOpeningBalance, reverseOpeningBalance, OPENING_MODES,
} from "../../services/openingBalanceService";

// D-314: who may enter opening balances = canDoAccounts() (purchaseBillService).
const MODE_LABEL = { cash: "Cash", bank: "Bank", upi: "UPI", cheque: "Cheque" };
const inr   = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const showDate = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "-";
};

const emptyParty = () => ({ party_type: "vendor", party_id: "", side: "due", amount: "", as_of: today(), notes: "" });
const emptyMode  = () => ({ mode_group: "bank", side: "positive", amount: "", as_of: today(), notes: "" });

export default function OpeningBalances() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const canEnter  = canDoAccounts({ ...user, role: String(user.role || "").toLowerCase().trim() });

  const [vendors,  setVendors]  = useState([]);
  const [subs,     setSubs]     = useState([]);
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [saving,   setSaving]   = useState(false);

  const [partyForm, setPartyForm] = useState(emptyParty());
  const [modeForm,  setModeForm]  = useState(emptyMode());
  const [partyId,   setPartyId]   = useState(newOpeningId());
  const [modeId,    setModeId]    = useState(newOpeningId());

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const [vSnap, sSnap, oSnap] = await Promise.all([
        getDocs(query(collection(db, "vendors"),        where("company_id", "==", companyId))),
        getDocs(query(collection(db, "subcontractors"), where("company_id", "==", companyId))),
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId), where("type", "==", "opening_balance"))),
      ]);
      const byName = (a, b) => a.name.localeCompare(b.name);
      setVendors(vSnap.docs.map(d => ({ id: d.id, name: d.data().vendor_name || "(no name)" })).sort(byName));
      setSubs(sSnap.docs.map(d => ({ id: d.id, name: d.data().name || "(no name)" })).sort(byName));
      setRows(oSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("OpeningBalances load error:", err);
      setError(err.message || "Failed to load opening balances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Entries with their reversal state (reversal rows themselves are not listed).
  const entries = useMemo(() => {
    const reversedBy = {};
    rows.filter(r => r.entry_kind === "reversal").forEach(r => { reversedBy[r.reverses_id] = r; });
    return rows
      .filter(r => r.entry_kind === "party" || r.entry_kind === "mode")
      .map(r => ({ ...r, reversal: reversedBy[r.id] || null }))
      .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
  }, [rows]);

  const partyEntries = entries.filter(e => e.entry_kind === "party");
  const modeEntries  = entries.filter(e => e.entry_kind === "mode");
  const partyList    = partyForm.party_type === "vendor" ? vendors : subs;

  const saveParty = async () => {
    if (saving) return;
    if (!partyForm.party_id) { alert("Select the vendor / subcontractor."); return; }
    const name = partyList.find(p => p.id === partyForm.party_id)?.name || "";
    const sideText = partyForm.side === "due" ? "We owe them" : "Advance given";
    if (!window.confirm(`${sideText}: ${name} — ${inr(partyForm.amount)} as of ${showDate(partyForm.as_of)}?\n\nThis cannot be edited later (only reversed).`)) return;
    setSaving(true);
    try {
      const res = await recordOpeningBalance({
        entry_id: partyId, company_id: companyId, entry_kind: "party",
        party_type: partyForm.party_type, party_id: partyForm.party_id,
        side: partyForm.side, amount: partyForm.amount, as_of: partyForm.as_of,
        notes: partyForm.notes,
        created_by: user.uid || user.id || "", created_by_name: user.name || "",
      });
      if (res.already_recorded) alert("This entry was already saved.");
      setPartyForm(f => ({ ...emptyParty(), party_type: f.party_type, as_of: f.as_of }));
      setPartyId(newOpeningId());
      await load();
    } catch (err) {
      alert(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveMode = async () => {
    if (saving) return;
    const sideText = modeForm.side === "positive" ? "Balance" : "Overdraft";
    if (!window.confirm(`${MODE_LABEL[modeForm.mode_group]} ${sideText}: ${inr(modeForm.amount)} as of ${showDate(modeForm.as_of)}?\n\nThis cannot be edited later (only reversed).`)) return;
    setSaving(true);
    try {
      const res = await recordOpeningBalance({
        entry_id: modeId, company_id: companyId, entry_kind: "mode",
        mode_group: modeForm.mode_group, side: modeForm.side,
        amount: modeForm.amount, as_of: modeForm.as_of, notes: modeForm.notes,
        created_by: user.uid || user.id || "", created_by_name: user.name || "",
      });
      if (res.already_recorded) alert("This entry was already saved.");
      setModeForm(f => ({ ...emptyMode(), as_of: f.as_of }));
      setModeId(newOpeningId());
      await load();
    } catch (err) {
      alert(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reverse = async (entry) => {
    if (saving) return;
    const reason = window.prompt("Reason for reversing this entry (required):");
    if (reason === null) return;
    if (!reason.trim()) { alert("Reason is required."); return; }
    setSaving(true);
    try {
      const res = await reverseOpeningBalance({
        company_id: companyId, original_id: entry.id, reason,
        created_by: user.uid || user.id || "", created_by_name: user.name || "",
      });
      if (res.already_reversed) alert("This entry was already reversed.");
      await load();
    } catch (err) {
      alert(err.message || "Failed to reverse");
    } finally {
      setSaving(false);
    }
  };

  const describe = (e) => e.entry_kind === "party"
    ? `${e.party_name || "-"} · ${e.party_type === "vendor" ? "Vendor" : "Subcontractor"} · ${e.side === "due" ? "We owe them" : "Advance given"}`
    : `${MODE_LABEL[e.mode_group] || e.mode_group} · ${e.side === "negative" ? "Overdraft" : "Balance"}`;

  const EntryList = ({ list }) => (
    <div className="space-y-2 mt-4">
      {list.length === 0 && <p className="text-xs text-slate-400">No entries yet.</p>}
      {list.map(e => (
        <div key={e.id} className={`rounded-xl border p-3 flex justify-between items-center gap-3 ${e.reversal ? "bg-slate-50 border-slate-100 opacity-60" : "bg-white border-slate-200"}`}>
          <div className="min-w-0">
            <p className={`text-sm font-semibold text-slate-800 ${e.reversal ? "line-through" : ""}`}>{describe(e)}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              As of {showDate(e.as_of)}{e.created_by_name ? ` · by ${e.created_by_name}` : ""}{e.notes ? ` · ${e.notes}` : ""}
            </p>
            {e.reversal && (
              <p className="text-xs text-red-600 mt-0.5">Reversed{e.reversal.notes ? ` — ${e.reversal.notes}` : ""}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className={`text-sm font-bold ${e.reversal ? "text-slate-400 line-through" : "text-slate-800"}`}>{inr(e.amount)}</p>
            {!e.reversal && canEnter && (
              <button onClick={() => reverse(e)} disabled={saving}
                className="text-xs text-red-600 hover:text-red-800 font-semibold mt-1 disabled:opacity-40">
                Reverse
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const input = "w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white disabled:opacity-50";
  const label = "text-xs font-semibold text-slate-500 uppercase mb-1 block";

  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen max-w-3xl mx-auto">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Opening Balances</h1>
      <p className="text-xs text-slate-500 mb-4">
        What you owed, and the money you had, on the day you started using Zeno.
        Not counted as income or expense. Entries cannot be edited — reverse a wrong entry and enter it again.
      </p>

      {!canEnter && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-800 font-semibold">
          Only Owner / Accounts can enter opening balances.
        </div>
      )}
      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {/* ── 1. VENDOR / SUBCONTRACTOR ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-5">
        <h2 className="font-bold text-slate-800 mb-1">1. Vendors &amp; subcontractors</h2>
        <p className="text-xs text-slate-500 mb-4">Shows in Payables as the oldest bill (or as an advance).</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={label}>Type</label>
            <select value={partyForm.party_type} disabled={!canEnter}
              onChange={e => setPartyForm(f => ({ ...f, party_type: e.target.value, party_id: "" }))}
              className={input}>
              <option value="vendor">Vendor</option>
              <option value="subcontractor">Subcontractor</option>
            </select>
          </div>
          <div>
            <label className={label}>Name</label>
            <select value={partyForm.party_id} disabled={!canEnter}
              onChange={e => setPartyForm(f => ({ ...f, party_id: e.target.value }))}
              className={input}>
              <option value="">— Select —</option>
              {partyList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Balance</label>
            <select value={partyForm.side} disabled={!canEnter}
              onChange={e => setPartyForm(f => ({ ...f, side: e.target.value }))}
              className={input}>
              <option value="due">We owe them (due)</option>
              <option value="advance">We paid in advance</option>
            </select>
          </div>
          <div>
            <label className={label}>Amount (₹)</label>
            <input type="number" min="0" step="0.01" value={partyForm.amount} disabled={!canEnter}
              onChange={e => setPartyForm(f => ({ ...f, amount: e.target.value }))}
              className={input} />
          </div>
          <div>
            <label className={label}>As of date</label>
            <input type="date" value={partyForm.as_of} disabled={!canEnter}
              onChange={e => setPartyForm(f => ({ ...f, as_of: e.target.value }))}
              className={input} />
          </div>
          <div>
            <label className={label}>Note (optional)</label>
            <input value={partyForm.notes} disabled={!canEnter} placeholder="e.g. as per Tally on 31 Oct"
              onChange={e => setPartyForm(f => ({ ...f, notes: e.target.value }))}
              className={input} />
          </div>
        </div>
        {partyList.length === 0 && !loading && (
          <p className="text-xs text-amber-700 mt-2">
            No {partyForm.party_type === "vendor" ? "vendors" : "subcontractors"} yet — add them in the master first.
          </p>
        )}
        <button onClick={saveParty} disabled={!canEnter || saving || !partyForm.party_id || !(Number(partyForm.amount) > 0)}
          className="w-full mt-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-blue-700">
          {saving ? "Saving..." : "Save"}
        </button>

        {loading ? <p className="text-xs text-slate-400 mt-4">Loading...</p> : <EntryList list={partyEntries} />}
      </div>

      {/* ── 2. CASH / BANK / UPI / CHEQUE ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h2 className="font-bold text-slate-800 mb-1">2. Cash &amp; bank</h2>
        <p className="text-xs text-slate-500 mb-4">The Cash / Bank Book cards start from these amounts.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={label}>Mode</label>
            <select value={modeForm.mode_group} disabled={!canEnter}
              onChange={e => setModeForm(f => ({ ...f, mode_group: e.target.value }))}
              className={input}>
              {OPENING_MODES.map(m => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Balance</label>
            <select value={modeForm.side} disabled={!canEnter}
              onChange={e => setModeForm(f => ({ ...f, side: e.target.value }))}
              className={input}>
              <option value="positive">Money in account / hand</option>
              <option value="negative">Overdraft (owed to bank)</option>
            </select>
          </div>
          <div>
            <label className={label}>Amount (₹)</label>
            <input type="number" min="0" step="0.01" value={modeForm.amount} disabled={!canEnter}
              onChange={e => setModeForm(f => ({ ...f, amount: e.target.value }))}
              className={input} />
          </div>
          <div>
            <label className={label}>As of date</label>
            <input type="date" value={modeForm.as_of} disabled={!canEnter}
              onChange={e => setModeForm(f => ({ ...f, as_of: e.target.value }))}
              className={input} />
          </div>
          <div className="sm:col-span-2">
            <label className={label}>Note (optional)</label>
            <input value={modeForm.notes} disabled={!canEnter} placeholder="e.g. HDFC current account"
              onChange={e => setModeForm(f => ({ ...f, notes: e.target.value }))}
              className={input} />
          </div>
        </div>
        <button onClick={saveMode} disabled={!canEnter || saving || !(Number(modeForm.amount) > 0)}
          className="w-full mt-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-blue-700">
          {saving ? "Saving..." : "Save"}
        </button>

        {loading ? <p className="text-xs text-slate-400 mt-4">Loading...</p> : <EntryList list={modeEntries} />}
      </div>
    </div>
  );
}
